import React from 'react';
import {
  DocumentCategory,
  LoanApplicationDocument,
  UploadedByRole,
} from '@axo/shared/data-access/types';
import { ApplicantDataApplicantTabPanelSection } from '../ApplicationDataApplicantTabPanelLayout';
import { NonIdealState, Spinner } from '@blueprintjs/core';
import { DocumentItemView } from './DocumentItemView';
import styles from './DocumentsSection.module.css';
import { useLoanApplicationDocuments } from '@axo/shared/data-access/hooks';

interface DocumentsSectionProps {
  applicationID: string;
  coApplicantCustomerID: string | undefined;
  coApplicantPersonID: string | undefined;
}

export const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  applicationID,
  coApplicantCustomerID,
  coApplicantPersonID,
}) => {
  const { data: documents, isLoading } =
    useLoanApplicationDocuments(applicationID);
  const isCoApplicant = coApplicantCustomerID || coApplicantPersonID;
  const uploadedByRole = isCoApplicant
    ? UploadedByRole.CoApplicant
    : UploadedByRole.MainApplicant;

  const filteredDocuments = documents?.filter(
    (x) => x.CreatedByRole === uploadedByRole,
  );

  const documentsByCategory = filteredDocuments?.reduce((map, document) => {
    if (!map.has(document?.Category)) {
      map.set(document.Category, []);
    }
    map.get(document.Category)?.push(document);
    return map;
  }, new Map<string, LoanApplicationDocument[]>());

  const displayOrder = [
    DocumentCategory.Payslip,
    DocumentCategory.TaxReturn,
    DocumentCategory.PropertyValue,
    DocumentCategory.Other,
  ];

  const documentsByCategoryNormalized = new Map();

  for (const key of displayOrder) {
    if (documentsByCategory?.has(key)) {
      documentsByCategoryNormalized.set(key, documentsByCategory.get(key));
    }
  }

  function parsedCategoryName(category: DocumentCategory) {
    switch (category) {
      case DocumentCategory.Payslip:
        return 'Payslip';
      case DocumentCategory.TaxReturn:
        return 'Tax';
      case DocumentCategory.PropertyValue:
        return 'Property value';
      default:
        return 'Other';
    }
  }

  return (
    <ApplicantDataApplicantTabPanelSection title="Documents">
      {isLoading ? (
        <NonIdealState icon={<Spinner />} />
      ) : (
        <React.Fragment>
          {[...documentsByCategoryNormalized.entries()].map(
            ([category, documents]) => (
              <div key={category} className={styles.categoryContainer}>
                <label>{parsedCategoryName(category)}</label>
                {documents.map(
                  (document: LoanApplicationDocument, index: number) => (
                    <DocumentItemView
                      applicationID={applicationID}
                      document={document}
                      key={document.ID || index}
                    />
                  ),
                )}
              </div>
            ),
          )}
        </React.Fragment>
      )}
    </ApplicantDataApplicantTabPanelSection>
  );
};
